import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Fab from '@mui/material/Fab';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { RHFTextField } from '@tyro/core/form';
import { useFormValidator } from '@tyro/core/hooks';
import { toast } from '@tyro/core/toaster';
import { MarkdownView } from '@tyro/documentation';
import { useTranslation } from '@tyro/i18n';
import { ArrowUpIcon, OpenBookIcon } from '@tyro/icons';
import { useGenerateReport } from '@tyro/reporting/api/generate-report';
import { useLocation } from '@tyro/router/hooks';
import { AnimatePresence, m } from 'motion/react';
import { nanoid } from 'nanoid';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTyronePromptStream } from '../../../../api/tyrone/prompt';
import type {
  TyroneApplyWidgetsEventDetail,
  TyroneLayoutProposalMessage,
  TyroneMessageThread,
  TyroneRemoveWidgetsEventDetail,
} from './types';

const CUSTOM_REPORT_EDITOR_PATH = /^\/insights\/report-builder\/[^/]+\/?$/;
const LAYOUT_CONVERSATION_PREFIX = 'layout-';

/**
 * Layout-mode conversations carry `layout-`-prefixed ids (see the backend
 * `ReportLayoutGenerationService`), chat-mode conversations don't. If the
 * user starts in one mode and navigates such that the other mode is now
 * active, the in-memory conversationId still points at the OTHER mode's
 * server-side memory. We drop it lazily at submit time so the new turn
 * starts a fresh conversation on its own side, without disturbing the
 * still-displayed thread the user can navigate back to.
 */
function conversationIdForMode(
  id: string | undefined,
  isLayoutMode: boolean,
): string | undefined {
  if (!id) return undefined;
  const isLayoutId = id.startsWith(LAYOUT_CONVERSATION_PREFIX);
  return isLayoutId === isLayoutMode ? id : undefined;
}

const TRAILING_SOURCE_LINE = /\n?\s*Source\s*:.*$/i;

function stripTrailingSourceLine(text: string): string {
  return text.replace(TRAILING_SOURCE_LINE, '').trimEnd();
}

/**
 * The model emits source citations as plain text — `Title [space/page]` —
 * because Azure OpenAI deployments tend to refuse to produce custom-scheme
 * URLs in markdown links (RLHF guardrail). Convert that natural format into
 * a `[Title](docs:space/page)` markdown link so MarkdownView's TipTap pipeline
 * renders it as an anchor and the popover's click handler can intercept it.
 */
const SOURCE_CITATION_PATTERN =
  /([\p{L}\p{N}][\p{L}\p{N} '&\-/]*?)\s*\[([a-z0-9][a-z0-9-]*\/[a-z0-9][a-z0-9-]*)\]/gu;

function linkifyDocSources(text: string): string {
  return text.replace(
    SOURCE_CITATION_PATTERN,
    (_match, title: string, slug: string) => `[${title.trim()}](docs:${slug})`,
  );
}

interface FormState {
  prompt: string;
}

interface ConversationViewProps {
  messageThread: TyroneMessageThread;
  setMessageThread: React.Dispatch<React.SetStateAction<TyroneMessageThread>>;
  onTitleChange?: (title: string) => void;
  // When set, the conversation view auto-fires this prompt once on mount.
  // Used by features (e.g. the Schedule > Diagnose tab) that open Tyrone with
  // a seeded question.
  initialPrompt?: string;
  // Bumped each time a fresh tyrone:open event fires. Prompts dedupe by this
  // counter, not by prompt text, so dispatching the same prompt twice still
  // re-fires the auto-submit.
  initialPromptVersion?: number;
  // Page-context hints sent with every stream request in this conversation.
  pageContext?: import('../../../../api/tyrone/prompt').TyronePageContext;
}

export function ConversationView({
  messageThread,
  setMessageThread,
  onTitleChange,
  initialPrompt,
  initialPromptVersion,
  pageContext,
}: ConversationViewProps) {
  const { t } = useTranslation(['common', 'reports']);
  const { customShadows } = useTheme();
  const messageContainerRef = useRef<HTMLDivElement>(null);

  const { stream, isPending: isStreamPending } = useTyronePromptStream();
  const generateLayout = useGenerateReport();

  const location = useLocation();
  const isLayoutMode = CUSTOM_REPORT_EDITOR_PATH.test(location.pathname);

  const isPending = isLayoutMode ? generateLayout.isPending : isStreamPending;

  const { resolver, rules } = useFormValidator<FormState>();
  const { control, handleSubmit, watch, reset } = useForm<FormState>({
    resolver: resolver({
      prompt: rules.required(),
    }),
  });

  const submitLayoutPrompt = (prompt: string) => {
    const isNewConversation =
      !messageThread.conversationId && messageThread.messages.length === 0;
    if (isNewConversation) {
      onTitleChange?.(prompt);
    }
    const userMessageId = nanoid(8);
    const aiMessageId = nanoid(8);
    setMessageThread((prev) => ({
      ...prev,
      messages: [
        ...prev.messages,
        { id: userMessageId, by: 'user', text: prompt },
        { id: aiMessageId, by: 'ai', text: '' },
      ],
    }));
    reset();

    generateLayout
      .mutateAsync({
        prompt,
        conversationId: conversationIdForMode(
          messageThread.conversationId,
          true,
        ),
      })
      .then((result) => {
        setMessageThread((prev) => ({
          ...prev,
          conversationId: result.conversationId,
          messages: prev.messages.map((m) => {
            if (m.id !== aiMessageId) return m;
            if (result.kind === 'WIDGETS') {
              return {
                id: aiMessageId,
                by: 'ai',
                kind: 'layout-proposal',
                widgets: result.widgets,
                widgetCount: result.widgets.length,
                droppedCount: result.droppedCount,
                dashboardFilters: result.dashboardFilters,
                reasoning: result.reasoning,
              } satisfies TyroneLayoutProposalMessage;
            }
            if (m.kind === 'layout-proposal') return m;
            return {
              ...m,
              text: result.message,
              options: result.options.length > 0 ? result.options : undefined,
            };
          }),
        }));
      })
      .catch((error: unknown) => {
        const message =
          error instanceof Error
            ? error.message
            : t('common:snackbarMessages.errorFailed');
        setMessageThread((prev) => ({
          ...prev,
          messages: prev.messages.filter(
            (m) => m.id !== userMessageId && m.id !== aiMessageId,
          ),
        }));
        toast.error(message);
      });
  };

  const onLayoutSubmit = handleSubmit(({ prompt }) =>
    submitLayoutPrompt(prompt),
  );

  const submitStreamPrompt = (prompt: string) => {
    const isNewConversation =
      !messageThread.conversationId && messageThread.messages.length === 0;
    if (isNewConversation) {
      onTitleChange?.(prompt);
    }
    const userMessageId = nanoid(8);
    const aiMessageId = nanoid(8);
    setMessageThread((prev) => ({
      ...prev,
      messages: [
        ...(prev?.messages || []),
        { id: userMessageId, by: 'user', text: prompt },
        { id: aiMessageId, by: 'ai', text: '' },
      ],
    }));
    reset();

    void stream(
      {
        conversationId: conversationIdForMode(
          messageThread?.conversationId,
          false,
        ),
        prompt,
        pageContext,
      },
      {
        onConversation: (id) => {
          setMessageThread((prev) => ({ ...prev, conversationId: id }));
        },
        onDelta: (chunk) => {
          setMessageThread((prev) => ({
            ...prev,
            messages: prev.messages.map((msg) =>
              msg.id === aiMessageId && msg.kind !== 'layout-proposal'
                ? { ...msg, text: msg.text + chunk }
                : msg,
            ),
          }));
        },
        onSources: (sources) => {
          setMessageThread((prev) => ({
            ...prev,
            messages: prev.messages.map((msg) =>
              msg.id === aiMessageId && msg.kind !== 'layout-proposal'
                ? { ...msg, sources }
                : msg,
            ),
          }));
        },
        onError: () => {
          setMessageThread((prev) => ({
            ...prev,
            messages: prev.messages.filter(
              (msg) => msg.id !== userMessageId && msg.id !== aiMessageId,
            ),
          }));
          toast.error(t('common:snackbarMessages.errorFailed'));
        },
      },
    );
  };

  const onStreamSubmit = handleSubmit(({ prompt }) =>
    submitStreamPrompt(prompt),
  );

  // When a feature opens Tyrone with a pre-seeded prompt, fire it once per
  // event dispatch. Dedupe by version counter (not prompt text) so two
  // clicks on the same Ask-Tyrone button both re-submit. Seeded prompts are
  // always chat (stream) mode, never the report-layout builder.
  const seededVersionRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (
      isLayoutMode ||
      !initialPrompt ||
      initialPromptVersion == null ||
      seededVersionRef.current === initialPromptVersion
    )
      return;
    seededVersionRef.current = initialPromptVersion;
    submitStreamPrompt(initialPrompt);
    // submitStreamPrompt is captured fresh each render; only version triggers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPromptVersion]);

  useEffect(() => {
    if (messageContainerRef.current) {
      const lastMessage = messageContainerRef.current.lastElementChild;
      if (lastMessage) {
        lastMessage.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [messageThread.messages, isPending]);

  const onSubmit = isLayoutMode ? onLayoutSubmit : onStreamSubmit;
  const prompt = watch('prompt');

  return (
    <>
      <Box
        ref={messageContainerRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,

          '& .message': {
            maxWidth: '80%',
            p: 1.5,
            borderRadius: 2,
            boxShadow: customShadows.card,

            '&.user': {
              alignSelf: 'flex-end',
              backgroundColor: 'indigo.100',
            },

            '&.ai': {
              alignSelf: 'flex-start',
              backgroundColor: 'background.paper',
            },
          },
        }}
      >
        <AnimatePresence mode="popLayout" initial={false}>
          {messageThread.messages.map((msg, index) => (
            <Box
              key={msg.id}
              component={m.div}
              layoutId={`message-${index}`}
              initial={{ opacity: 0, scale: 0.9, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className={`message ${msg.by}`}
            >
              {msg.by === 'ai' && msg.kind === 'layout-proposal' ? (
                <LayoutProposalCard message={msg} />
              ) : msg.by === 'ai' ? (
                msg.text ? (
                  <Stack spacing={1.25}>
                    <MarkdownView
                      content={linkifyDocSources(
                        msg.sources?.length
                          ? stripTrailingSourceLine(msg.text)
                          : msg.text,
                      )}
                      onDocLinkClick={(spaceSlug, pageSlug) => {
                        window.dispatchEvent(
                          new CustomEvent('tyro:open-doc', {
                            detail: { spaceSlug, pageSlug },
                          }),
                        );
                      }}
                      sx={{
                        fontSize: '0.875rem',
                        '& .ProseMirror': { p: 0, minHeight: 0 },
                        '& .ProseMirror > :first-of-type': { mt: 0 },
                        '& .ProseMirror > :last-child': { mb: 0 },
                      }}
                    />
                    {msg.sources?.length ? (
                      <Stack
                        direction="row"
                        spacing={0.75}
                        sx={{ flexWrap: 'wrap', rowGap: 0.75 }}
                      >
                        {msg.sources.map((source) => (
                          <Chip
                            key={source.slug}
                            label={source.title}
                            size="small"
                            icon={<OpenBookIcon />}
                            clickable
                            onClick={() => {
                              const [spaceSlug, pageSlug] = source.slug.split(
                                '/',
                                2,
                              );
                              if (spaceSlug && pageSlug) {
                                window.dispatchEvent(
                                  new CustomEvent('tyro:open-doc', {
                                    detail: { spaceSlug, pageSlug },
                                  }),
                                );
                              }
                            }}
                            sx={{ fontSize: '0.75rem' }}
                          />
                        ))}
                      </Stack>
                    ) : null}
                    {msg.options?.length ? (
                      <Stack
                        direction="row"
                        spacing={0.75}
                        sx={{ flexWrap: 'wrap', rowGap: 0.75 }}
                      >
                        {msg.options.map((option) => (
                          <Button
                            key={option}
                            size="small"
                            variant="outlined"
                            disabled={isPending}
                            onClick={() => submitLayoutPrompt(option)}
                            sx={{ textTransform: 'none' }}
                          >
                            {option}
                          </Button>
                        ))}
                      </Stack>
                    ) : null}
                  </Stack>
                ) : (
                  <Stack direction="row" spacing={0.5}>
                    {[0, 1, 2].map((i) => (
                      <Box
                        key={i}
                        component={m.div}
                        animate={{ y: [0, -6, 0] }}
                        transition={{
                          duration: 0.6,
                          repeat: Number.POSITIVE_INFINITY,
                          delay: i * 0.1,
                          ease: 'easeInOut',
                        }}
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          backgroundColor: 'text.secondary',
                        }}
                      />
                    ))}
                  </Stack>
                )
              ) : (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {msg.text}
                </Typography>
              )}
            </Box>
          ))}
        </AnimatePresence>
      </Box>

      <Stack p={1} width="100%">
        <RHFTextField
          controlProps={{
            control,
            name: 'prompt',
          }}
          variant="white-filled-outlined"
          textFieldProps={{
            placeholder: 'Ask anything...',
            multiline: true,
            minRows: 2,
            maxRows: 4,
            fullWidth: true,
            slotProps: {
              input: {
                'aria-label': 'prompt',
                hiddenLabel: true,
                onKeyDown: (e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    onSubmit();
                  }
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <Fab
                      size="small"
                      aria-label={t('common:actions.send')}
                      onClick={onSubmit}
                      disabled={isPending || !(prompt?.length > 0)}
                    >
                      <ArrowUpIcon />
                    </Fab>
                  </InputAdornment>
                ),
              },
            },
          }}
        />
      </Stack>
    </>
  );
}

type WidgetMeta = {
  title?: string;
  reportId?: string;
  metricId?: string;
  groupings?: { fieldId: string; timeBucket?: string }[];
};

type WidgetSummary = {
  id: string;
  type: string;
  meta?: WidgetMeta;
};

const WIDGET_TYPE_LABEL: Record<string, string> = {
  'report-figures': 'Figures',
  'report-chart': 'Chart',
  'report-table': 'Table',
  'dashboard-awol': 'AWOL',
  'dashboard-behaviour': 'Behaviour',
  'dashboard-overdue-attendance': 'Overdue attendance',
  'dashboard-priority-students': 'Priority students',
  'dashboard-highlights': 'Highlights',
  'dashboard-assessments': 'Assessments',
  'dashboard-timetable': 'Timetable',
  'dashboard-school-calendar': 'School calendar',
  'dashboard-staff-drive': 'Staff drive',
};

function widgetLabel(widget: WidgetSummary): string {
  if (widget.meta?.title) return widget.meta.title;
  if (widget.meta?.metricId) return widget.meta.metricId;
  if (widget.meta?.reportId) return widget.meta.reportId;
  return WIDGET_TYPE_LABEL[widget.type] ?? widget.type;
}

function widgetDetail(widget: WidgetSummary): string | null {
  const groupings = widget.meta?.groupings;
  if (!groupings || groupings.length === 0) return null;
  return groupings
    .map((grouping) =>
      grouping.timeBucket
        ? `${grouping.fieldId} (${grouping.timeBucket.toLowerCase()})`
        : grouping.fieldId,
    )
    .join(', ');
}

function LayoutProposalCard({
  message,
}: {
  message: TyroneLayoutProposalMessage;
}) {
  const { t } = useTranslation(['reports']);
  const [applied, setApplied] = useState(false);
  const summaries = message.widgets as WidgetSummary[];
  const filterIds = Object.keys(message.dashboardFilters ?? {});

  const handleApply = () => {
    const detail: TyroneApplyWidgetsEventDetail = {
      widgets: message.widgets,
      dashboardFilters: message.dashboardFilters,
    };
    window.dispatchEvent(
      new CustomEvent<TyroneApplyWidgetsEventDetail>('tyro:apply-widgets', {
        detail,
      }),
    );
    setApplied(true);
  };
  const handleUndo = () => {
    const detail: TyroneRemoveWidgetsEventDetail = {
      widgetIds: summaries.map((widget) => widget.id),
    };
    window.dispatchEvent(
      new CustomEvent<TyroneRemoveWidgetsEventDetail>('tyro:remove-widgets', {
        detail,
      }),
    );
    setApplied(false);
  };
  return (
    <Stack spacing={1}>
      {message.reasoning ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontStyle: 'italic' }}
        >
          {message.reasoning}
        </Typography>
      ) : null}
      <Typography variant="body2">
        {t('reports:tyroneLayoutReady', { count: message.widgetCount })}
        {message.droppedCount > 0
          ? ` ${t('reports:tyroneLayoutDropped', { count: message.droppedCount })}`
          : ''}
      </Typography>
      <Stack
        component="ul"
        spacing={0.5}
        sx={{ m: 0, pl: 0, listStyle: 'none' }}
      >
        {summaries.map((widget) => {
          const detail = widgetDetail(widget);
          return (
            <Stack
              key={widget.id}
              component="li"
              direction="row"
              alignItems="baseline"
              spacing={0.75}
            >
              <Chip
                label={WIDGET_TYPE_LABEL[widget.type] ?? widget.type}
                size="small"
                sx={{
                  fontSize: '0.7rem',
                  height: 18,
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, minWidth: 0, flex: 1 }}
                noWrap
              >
                {widgetLabel(widget)}
              </Typography>
              {detail && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ minWidth: 0 }}
                  noWrap
                >
                  by {detail}
                </Typography>
              )}
            </Stack>
          );
        })}
      </Stack>
      {filterIds.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          {t('reports:tyroneLayoutFilters', {
            filters: filterIds.join(', '),
          })}
        </Typography>
      )}
      {applied ? (
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          onClick={handleUndo}
          sx={{ alignSelf: 'flex-start' }}
        >
          {t('reports:tyroneUndoLayout')}
        </Button>
      ) : (
        <Button
          size="small"
          variant="contained"
          onClick={handleApply}
          sx={{ alignSelf: 'flex-start' }}
        >
          {t('reports:tyroneApplyLayout')}
        </Button>
      )}
    </Stack>
  );
}
